import { useState, useImperativeHandle, useCallback } from "react";
import { ValidationGroupContext } from "./ValidationGroupContext";
import type { Validation } from "@/types/Validations";
import { getCurrentStateValues } from "@/stores";
import { type ValidationGroupProviderProps } from "./ValidationGroupProviderProps";
import { focusAndScrollIntoView } from "@/utils/dom";

export function ValidationGroupProvider({
  children,
  ref,
  name,
  isValidated: isValidatedProp = false,
  validate: asyncValidate,
}: ValidationGroupProviderProps) {
  const [isValidated, setIsValidated] = useState(isValidatedProp);
  const [validations, setValidations] = useState<Record<string, Validation>>(
    {},
  );
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  useImperativeHandle(ref, () => ({
    validate: async () => {
      setIsValidated(true);
      let firstInvalidElement: HTMLElement | null = null;
      let isValid = true;
      const currentState = getCurrentStateValues();
      const errors = {} as Record<string, string | null>;

      for (const [name, validation] of Object.entries(validations)) {
        const result = validation.validate(currentState);
        if (!result.isValid) {
          isValid = false;
          errors[name] = result.error!;
        } else {
          errors[name] = null;
        }

        if (!result.isValid && !firstInvalidElement) {
          firstInvalidElement = validation.ref?.current ?? null;
        }
      }

      // The async check is a network call (the bank details API). Skip it
      // while a sync check fails: the values are already wrong, and the donor
      // must not wait for the network to see where the problem is.
      if (isValid && asyncValidate) {
        const asyncResult = await asyncValidate(currentState);

        if (asyncResult.errors) {
          Object.entries(asyncResult.errors).forEach(
            ([fieldName, errorMessage]) => {
              errors[fieldName] = errorMessage;
            },
          );
        }

        if (!asyncResult.isValid) {
          isValid = false;
        }
      }

      setErrors(errors);
      focusAndScrollIntoView(firstInvalidElement);

      return { isValid };
    },
  }));

  const registerValidation = useCallback(
    (name: string, validation: Validation): (() => void) => {
      setValidations((prev) => ({ ...prev, [name]: validation }));

      return () => {
        setValidations((prev) => {
          const newValidations = { ...prev };
          delete newValidations[name];
          return newValidations;
        });
      };
    },
    [],
  );

  return (
    <ValidationGroupContext.Provider
      value={{
        isValidated,
        registerValidation,
        groupName: name,
        errors,
        setErrors,
      }}
    >
      {children}
    </ValidationGroupContext.Provider>
  );
}
